/**
 * marketHolidays.js
 * Generador PROGRAMÁTICO de festivos bursátiles para cualquier año.
 * Cubre tres mercados:
 *   - 'EU' → BME (Madrid) / Xetra (Frankfurt) / Euronext (festivos comunes)
 *   - 'US' → NYSE / NASDAQ
 *   - 'MX' → BMV (Bolsa Mexicana de Valores)
 *
 * Devuelve un Map<'YYYY-MM-DD', nombreES>. Los resultados se cachean por
 * (mercado + año). Al ser programático NO hay que mantener listas a mano.
 */

const pad = (n) => String(n).padStart(2, '0');
const keyOf = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
// Día de la semana (0=domingo … 6=sábado) en UTC para evitar desfases de zona.
const dow = (y, m, d) => new Date(Date.UTC(y, m - 1, d)).getUTCDay();

// Suma días a una fecha (y/m/d) y devuelve {y,m,d}.
function addDays(y, m, d, delta) {
  const dt = new Date(Date.UTC(y, m - 1, d + delta));
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

// N-ésimo día de la semana del mes (weekday 0=dom … 6=sáb).
function nthWeekday(y, m, weekday, n) {
  const first = dow(y, m, 1);
  return 1 + ((7 + weekday - first) % 7) + (n - 1) * 7;
}
// Último día de la semana del mes.
function lastWeekday(y, m, weekday) {
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const last = dow(y, m, daysInMonth);
  return daysInMonth - ((7 + last - weekday) % 7);
}

// Domingo de Pascua (rito occidental, algoritmo de Gauss/Anónimo Gregoriano).
function easterSunday(y) {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
  const mth = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * mth + 114) / 31);
  const day = ((h + l - 7 * mth + 114) % 31) + 1;
  return { m: month, d: day };
}

// Festivos EE.UU. observan traslado: sábado→viernes, domingo→lunes.
function usObserved(y, m, d) {
  const w = dow(y, m, d);
  if (w === 6) return addDays(y, m, d, -1);
  if (w === 0) return addDays(y, m, d, 1);
  return { y, m, d };
}

function buildUS(y) {
  const e = easterSunday(y);
  const gf = addDays(y, e.m, e.d, -2); // Viernes Santo
  const nyd = usObserved(y, 1, 1);
  const jun = usObserved(y, 6, 19);
  const ind = usObserved(y, 7, 4);
  const xmas = usObserved(y, 12, 25);
  return [
    [keyOf(nyd.y, nyd.m, nyd.d), 'Año Nuevo'],
    [keyOf(y, 1, nthWeekday(y, 1, 1, 3)), 'Día de Martin Luther King'],
    [keyOf(y, 2, nthWeekday(y, 2, 1, 3)), 'Día de los Presidentes'],
    [keyOf(gf.y, gf.m, gf.d), 'Viernes Santo'],
    [keyOf(y, 5, lastWeekday(y, 5, 1)), 'Día de los Caídos'],
    [keyOf(jun.y, jun.m, jun.d), 'Juneteenth'],
    [keyOf(ind.y, ind.m, ind.d), 'Día de la Independencia'],
    [keyOf(y, 9, nthWeekday(y, 9, 1, 1)), 'Día del Trabajo (EE.UU.)'],
    [keyOf(y, 11, nthWeekday(y, 11, 4, 4)), 'Acción de Gracias'],
    [keyOf(xmas.y, xmas.m, xmas.d), 'Navidad'],
  ];
}

function buildEU(y) {
  const e = easterSunday(y);
  const gf = addDays(y, e.m, e.d, -2); // Viernes Santo
  const em = addDays(y, e.m, e.d, 1);  // Lunes de Pascua
  return [
    [keyOf(y, 1, 1), 'Año Nuevo'],
    [keyOf(gf.y, gf.m, gf.d), 'Viernes Santo'],
    [keyOf(em.y, em.m, em.d), 'Lunes de Pascua'],
    [keyOf(y, 5, 1), 'Día del Trabajo'],
    [keyOf(y, 12, 24), 'Nochebuena'],
    [keyOf(y, 12, 25), 'Navidad'],
    [keyOf(y, 12, 31), 'Nochevieja'],
  ];
}

function buildMX(y) {
  const e = easterSunday(y);
  const js = addDays(y, e.m, e.d, -3); // Jueves Santo
  const vs = addDays(y, e.m, e.d, -2); // Viernes Santo
  return [
    [keyOf(y, 1, 1), 'Año Nuevo'],
    [keyOf(y, 2, nthWeekday(y, 2, 1, 1)), 'Día de la Constitución'],
    [keyOf(y, 3, nthWeekday(y, 3, 1, 3)), 'Natalicio de Benito Juárez'],
    [keyOf(js.y, js.m, js.d), 'Jueves Santo'],
    [keyOf(vs.y, vs.m, vs.d), 'Viernes Santo'],
    [keyOf(y, 5, 1), 'Día del Trabajo'],
    [keyOf(y, 9, 16), 'Día de la Independencia'],
    [keyOf(y, 11, nthWeekday(y, 11, 1, 3)), 'Día de la Revolución'],
    [keyOf(y, 12, 12), 'Día de la Virgen de Guadalupe'],
    [keyOf(y, 12, 25), 'Navidad'],
  ];
}

const BUILDERS = { US: buildUS, EU: buildEU, MX: buildMX };
const _cache = new Map(); // 'MARKET:YEAR' → Map

export function getMarketHolidays(market, year) {
  const mk = (market || 'US').toUpperCase();
  const cacheKey = `${mk}:${year}`;
  if (_cache.has(cacheKey)) return _cache.get(cacheKey);
  const builder = BUILDERS[mk] || buildUS;
  const map = new Map(builder(year));
  _cache.set(cacheKey, map);
  return map;
}

// Próximos festivos (por defecto 6) a partir de hoy (o de una fecha YYYY-MM-DD).
export function getUpcomingHolidays(market, count = 6, fromKey = null) {
  const today = fromKey || new Date().toISOString().slice(0, 10);
  const y = parseInt(today.slice(0, 4), 10) || new Date().getUTCFullYear();
  const all = [];
  for (const yr of [y, y + 1]) {
    for (const [k, name] of getMarketHolidays(market, yr)) all.push({ date: k, name });
  }
  return all
    .filter((h) => h.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, count);
}
